import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { ProjectService } from '../services/project.service';
import {
  CursorModifier,
  EAutoRange,
  EAxisAlignment,
  ELabelPlacement,
  ELegendOrientation,
  ENumericFormat,
  FastLineRenderableSeries,
  HeatmapColorMap,
  HeatmapLegend,
  LegendModifier,
  NumberRange,
  NumericAxis,
  RolloverModifier,
  RubberBandXyZoomModifier,
  SciChartSurface,
  TSciChart,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  XyDataSeries,
  ZoomPanModifier,
  formatUnixDateToHumanStringHHMMSS,
  zeroArray2D,
} from 'scichart';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ZoneDataRow } from '../models/zone-data-row';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { MarkerInfo } from '../models/marker-info';
import { ChartCaptureService } from '../services/chart-capture.service';

@Component({
  selector: 'app-graph',
  templateUrl: './graph.component.html',
  styleUrls: ['./graph.component.scss'],
})
export class GraphComponent implements OnInit, OnDestroy {
  @Input() isSubView: boolean = false;
  errorMessage = '';
  tempDataLoaded: boolean = false;
  zoneDataLoaded: boolean = false;

  // Temp Chart
  temperatureDataRows: TemperatureDataRow[] = [];
  xyDataSeries3dLeft: XyDataSeries = {} as XyDataSeries;
  tick: number = 0;
  tickMax: number = 0;
  markerPosition3d: number = 0;
  tempData: ProjectTemperatureData = {} as ProjectTemperatureData;
  // Used for Vertical Time Labels
  temp_startUnixTime: number = 0;
  sciChartSurface_left?: SciChartSurface; // Store reference to left chart surface
  sciChartSurface_right?: SciChartSurface; // Store reference to right chart surface
  isInitialLoad: boolean = true; // Track if this is the first load

  // Zone Chart
  zoneDataRows: ZoneDataRow[] = [];
  heatmapDataSeries1: UniformHeatmapDataSeries = {} as UniformHeatmapDataSeries;

  rolloverModifier_left?: RolloverModifier;
  rolloverModifier?: RolloverModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // TODO: get from Api
  // channelrange_start: number = 0;
  // channelrange_end: number = 37;
  // channelrange_span: number = 37;
  // samplingInterval: number = 0.15;
  // measurementInterval: number = 1;
  channelrange_start: number = 0;
  channelrange_end: number = 4;
  channelrange_span: number = 4;
  samplingInterval: number = 1;
  measurementInterval: number = 1;
  heatMaps: number[][] = [];
  isRunning: boolean = false;

  rangeSliderLeftVal: number = 0;
  rangeSliderRightVal: number = 100;
  left_rangeSliderTopVal: number = 0;
  left_rangeSliderBotVal: number = 100;
  right_rangeSliderLeftVal: number = 0;
  right_rangeSliderRightVal: number = 100;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;
  public onRangeSliderChange?: (left: number, right: number) => void;
  public left_onRangeSliderChange?: (left: number, right: number) => void;
  public right_onRangeSliderChange?: (left: number, right: number) => void;
  public right2_onRangeSliderChange?: (left: number, right: number) => void;

  selectedProjectSubscription: Subscription = {} as Subscription;
  tempDataSubscription: Subscription = {} as Subscription;
  tempRTDataSubscription: Subscription = {} as Subscription;
  zoneRTDataSubscription: Subscription = {} as Subscription;
  tickSubscription: Subscription = {} as Subscription;
  vert_annot_positionsSubscription: Subscription = {} as Subscription;
  markers: MarkerInfo[] = [];

  showRangeSlider: boolean = false;

  constructor(public projectService: ProjectService, private chartCapture: ChartCaptureService) {}

  ngOnInit() {
    this.projectService.isHelpOverlayVisible = false;
    this.initSciChart();
    this.setupProjectSubscription();
    this.createTempAndWaterfallGraphs();
    this.createTempLegend();
  }

  ngOnDestroy(): void {
    // Dispose of the chart surfaces to free up resources
    if (this.sciChartSurface_left) {
      this.chartCapture.unregister('temperatureOverTime', this.sciChartSurface_left);
      this.sciChartSurface_left.delete();
    }
    if (this.sciChartSurface_right) {
      this.chartCapture.unregister('heatmap3D', this.sciChartSurface_right);
      this.sciChartSurface_right.delete();
    }
    
    this.selectedProjectSubscription.unsubscribe();
    this.tempDataSubscription.unsubscribe();
    this.tempRTDataSubscription.unsubscribe();
    this.zoneRTDataSubscription.unsubscribe();
    //this.tickSubscription.unsubscribe();
    this.vert_annot_positionsSubscription.unsubscribe();
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          if (projectName && projectName.length > 0) {
            console.log('Graph > Project changed to:', projectName);
            console.log('Graph > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Graph > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graphs with the new project's unit settings
            this.recreateGraphs();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the graphs (called when project changes)
  async recreateGraphs() {
    console.log('Graph > Recreating graphs with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
    
    // Dispose of the existing charts if they exist
    if (this.sciChartSurface_left) {
      console.log('Graph > Disposing existing left chart');
      this.sciChartSurface_left.delete();
      this.sciChartSurface_left = undefined;
    }
    if (this.sciChartSurface_right) {
      console.log('Graph > Disposing existing right chart');
      this.sciChartSurface_right.delete();
      this.sciChartSurface_right = undefined;
    }

    // Clear data
    this.heatMaps = [];
    this.heatmapDataSeries1 = {} as UniformHeatmapDataSeries;

    // Recreate the charts
    await this.createTempAndWaterfallGraphs();
    await this.createTempLegend();
  }

  async initSciChart() {
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Temperatere Graph ===========================================================================

  async createTempAndWaterfallGraphs() {
    console.log('Graph > Creating graphs with units:', this.projectService.isMetricUnit ? 'Metric (m, °C)' : 'Imperial (ft, °F)');
    
    // Left Temp Over Time Graph
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      'temp-over-time-graph'
    );

    // Store reference to the left chart surface
    this.sciChartSurface_left = sciChartSurface;
    this.chartCapture.register('temperatureOverTime', sciChartSurface, 'temp-over-time-graph');

    // X axis = Time (bottom), Y axis = Temperature (left)
    const xAxis = new NumericAxis(wasmContext, {
      autoRange: EAutoRange.Once,
      drawLabels: true,
      axisAlignment: EAxisAlignment.Bottom,
    });

    xAxis.labelProvider.formatLabel = this.formatDurationInSecToHHMMSS_Custom2;

    const yAxis = new NumericAxis(wasmContext, {
      autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
      axisAlignment: EAxisAlignment.Left,
    });

    yAxis.growBy = new NumberRange(0.2, 0.2);

    sciChartSurface.xAxes.add(xAxis);
    sciChartSurface.yAxes.add(yAxis);

    sciChartSurface.chartModifiers.add(
      new LegendModifier({
        showCheckboxes: false,
        orientation: ELegendOrientation.Vertical,
      })
    );
    const cursorModifier = new CursorModifier({
      crosshairStroke: '#ff6600',
      crosshairStrokeThickness: 1,
      tooltipContainerBackground: '#000',
      tooltipTextStroke: '#ff6600',
      //showTooltip: true,
      axisLabelFill: '#b36200',
      axisLabelStroke: '#fff',
      //showTooltip: false,
      showYLine: false,
    });

    sciChartSurface.chartModifiers.add(cursorModifier);

    const customTemplate = (heatmapSeries3d: any, tooltipTitle: any) => {
      const valuesWithLabels = [];
      if (tooltipTitle) {
        valuesWithLabels.push(tooltipTitle);
      }
      const heatmapSeriesInfo = heatmapSeries3d;

      valuesWithLabels.push(`${heatmapSeriesInfo.formattedYValue}`);

      return valuesWithLabels;
    };
    // Create new modifier instance to avoid "already attached" errors
    this.rolloverModifier_left = new RolloverModifier({
      tooltipDataTemplate: customTemplate,
    });
    sciChartSurface.chartModifiers.add(this.rolloverModifier_left);

    // Right Waterfall Graph ========================================================

    const { sciChartSurface: sciChartSurface2, wasmContext: wasmContext2 } =
      await SciChartSurface.create('waterfall-graph');

    // Store reference to the right chart surface
    this.sciChartSurface_right = sciChartSurface2;
    this.chartCapture.register('heatmap3D', sciChartSurface2, 'waterfall-graph');

    // Create an X,Y Axis and add to the chart
    const xAxis2 = new NumericAxis(wasmContext2, {
      autoRange: EAutoRange.Once,
      drawLabels: true,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      majorDelta: 20,
      minorDelta: 20,
      autoTicks: false,
    });

    const yAxis2 = new NumericAxis(wasmContext2, {
      autoRange: EAutoRange.Once,
      drawLabels: true,
      flippedCoordinates: true,
      axisAlignment: EAxisAlignment.Left,
    });

    yAxis2.labelProvider.formatLabel = this.formatDurationInSecToHHMMSS_Custom2;

    sciChartSurface2.xAxes.add(xAxis2);
    sciChartSurface2.yAxes.add(yAxis2);

    // Event Subscriptions =======================================================
    // Note: Don't subscribe to selectedProject here as it's handled in setupProjectSubscription()

    // Unsubscribe from previous subscriptions if they exist to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.temperatureData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Graph Comp: Temp data is null.');
          return;
        } else if (data.TRC == null) {
          console.log('Error in Graph Comp: Temp data > TRC is null.');
          return;
        } else if (data.TRC.length == 0) {
          console.log(
            'Error in Graph Comp: Temp data > TRC collection has no value.'
          );
          return;
        }

        this.tempData = data;
        this.temperatureDataRows = data.TRC;
        this.samplingInterval = data.SI;
        this.measurementInterval = data.MI;
        this.channelrange_start = data.CRB;
        this.channelrange_end = data.CRE;
        this.channelrange_span = data.CRS;

        //console.log('projectService.temperatureData.subscribe. Tick to zero');
        this.updateTempChartForMarkers(sciChartSurface, wasmContext);
        sciChartSurface.zoomExtents();

        // Waterfall Graph Setup Data Refresh ===================================
        this.updateWaterfallChart(sciChartSurface2, wasmContext2);

        this.setupZoneDataSeries(
          sciChartSurface2,
          wasmContext2,
          this.heatmapDataSeries1
        );
      },
      (error) => (this.errorMessage = error)
    );

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempRTDataSubscription && typeof this.tempRTDataSubscription.unsubscribe === 'function') {
      this.tempRTDataSubscription.unsubscribe();
    }
    this.tempRTDataSubscription =
      this.projectService.temperatureRTData.subscribe(
        (data) => {
          console.log('Graph comp > temperatureRTData.subscribe');
          if (data == null) {
            console.log('Error in Graph Comp: Temp RT data is null.');
            return;
          } else if (data.TRC == null) {
            console.log('Error in Graph Comp: Temp RT data > TRC is null.');
            return;
          }

          if (data.TRC.length > 0) {
            this.temperatureDataRows.push(data.TRC[0]);

            console.log(
              'Graph comp > pushed next TimeStamp Data. Count: ' +
                this.temperatureDataRows.length
            );

            //console.log('projectService.temperatureData.subscribe. Tick to zero');
            this.updateTempChartForMarkers(sciChartSurface, wasmContext);
            sciChartSurface.zoomExtents();

            // Waterfall Graph Setup Data Refresh ===================================
            this.updateWaterfallChart(sciChartSurface2, wasmContext2);
          }
        },
        (error) => (this.errorMessage = error)
      );

    // Zone RT Data Subscription for Heatmap/Waterfall updates
    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.zoneRTDataSubscription && typeof this.zoneRTDataSubscription.unsubscribe === 'function') {
      this.zoneRTDataSubscription.unsubscribe();
    }
    this.zoneRTDataSubscription = this.projectService.zoneRTData.subscribe(
      (data) => {
        console.log('Graph comp > zoneRTData.subscribe');
        if (data == null) {
          console.log('Error in Graph Comp: Zone RT data is null.');
          return;
        } else if (data.ZRC == null) {
          console.log('Error in Graph Comp: Zone RT data > ZRC is null.');
          return;
        }

        if (data.ZRC.length > 0) {
          this.zoneDataRows.push(data.ZRC[0]);
          
          console.log(
            'Graph comp > pushed next Zone TimeStamp Data. Count: ' +
              this.zoneDataRows.length
          );

          // Update Waterfall/Heatmap Chart with new zone data
          this.updateWaterfallChart(sciChartSurface2, wasmContext2);
          
          // Update zone data series for heatmap
          this.setupZoneDataSeries(
            sciChartSurface2,
            wasmContext2,
            this.heatmapDataSeries1
          );
        }
      },
      (error) => (this.errorMessage = error)
    );

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     if (this.temperatureDataRows.length > 0) {
    //       const endPosition =
    //         data == 0 ? this.temperatureDataRows.length - 1 : data;

    //       const endTimeDuration = this.getTimeDiffInSec(
    //         this.temperatureDataRows[0].UT,
    //         this.temperatureDataRows[endPosition].UT
    //       );

    //       // Temp Graph Navigation
    //       xAxis.visibleRange = new NumberRange(0, endTimeDuration);

    //       // Waterfall Graph Navigation
    //       yAxis2.visibleRange = new NumberRange(0, endTimeDuration);
    //     }
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // Listen to Control Events:
    this.onZoomOut = () => {
      xAxis2.visibleRange = new NumberRange(
        0,
        this.channelrange_end - this.channelrange_start
      );

      const endPosition = this.temperatureDataRows.length - 1;

      const endTimeDuration = this.getTimeDiffInSec(
        this.temperatureDataRows[0].UT,
        this.temperatureDataRows[endPosition].UT
      );
      yAxis2.visibleRange = new NumberRange(0, endTimeDuration);
    };

    this.onRangeSliderChange = (left, right) => {
      //console.log('bottom:' + left + '-' + right);
      const linerLength = this.tempData.CRE - this.tempData.CRB;
      const visibleLeft = Math.round((left / 100) * linerLength);
      const visibleRight = Math.round((right / 100) * linerLength);
      xAxis2.visibleRange = new NumberRange(visibleLeft, visibleRight);
    };

    this.left_onRangeSliderChange = (top, bot) => {
      //console.log('left:' + top + '-' + bot);
      if (this.temperatureDataRows.length > 0) {
        const endPosition = this.temperatureDataRows.length - 1;

        const endTimeDuration = this.getTimeDiffInSec(
          this.temperatureDataRows[0].UT,
          this.temperatureDataRows[endPosition].UT
        );

        const visibleLeft = Math.round((top / 100) * endTimeDuration);
        const visibleRight = Math.round((bot / 100) * endTimeDuration);
        xAxis.visibleRange = new NumberRange(visibleLeft, visibleRight);
        yAxis2.visibleRange = new NumberRange(visibleLeft, visibleRight);
      }
    };

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.vert_annot_positionsSubscription && typeof this.vert_annot_positionsSubscription.unsubscribe === 'function') {
      this.vert_annot_positionsSubscription.unsubscribe();
    }
    this.vert_annot_positionsSubscription =
      this.projectService.vert_annot_positions.subscribe(
        (data) => {
          this.markers = data;
          this.updateVerticalAnnotations(sciChartSurface2, data);
          this.updateTempChartForMarkers(sciChartSurface, wasmContext);
        },
        (error) => (this.errorMessage = error)
      );

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };
  }

  updateTempChartForMarkers(sciChart: SciChartSurface, wasmContext: TSciChart) {
    if (this.temperatureDataRows.length == 0) {
      this.tempDataLoaded = false;
      return;
    } else {
      this.tempDataLoaded = true;
    }

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    const positions = this.projectService.getVerticalAnnotationPositions();

    sciChart.renderableSeries.clear();

    for (let m = 0; m < positions.length; m++) {
      const pos = positions.length > 0 ? positions[m].P : 0;
      if (pos == 0) {
        console.log('no marker');
        continue;
      }

      const series = new XyDataSeries(wasmContext);
      const unitSuffix = this.projectService.isMetricUnit ? 'm' : 'ft';
      series.dataSeriesName = `Marker at ${pos.toFixed(1)}${unitSuffix}`;

      const lineSeries = new FastLineRenderableSeries(wasmContext, {
        stroke: this.projectService.getMarkerColorForIndex(m),
        strokeThickness: 2.4,
        dataSeries: series,
      });
      sciChart.renderableSeries.add(lineSeries);

      const percAtMarker = pos / (this.tempData.CRE - this.tempData.CRB);

      const dataPointIndexAtPerc =
        Math.round(percAtMarker * (endDataPoint - startDataPoint)) +
        startDataPoint;

      var xValues: number[] = new Array(this.temperatureDataRows.length);
      var yValues: number[] = new Array(this.temperatureDataRows.length);

      // Update Temp Graph

      for (let t = 0; t < this.temperatureDataRows.length; t++) {
        xValues[t] = this.getTimeDiffInSec(
          this.temperatureDataRows[0].UT,
          this.temperatureDataRows[t].UT
        );
        yValues[t] = this.temperatureDataRows[t].TR[dataPointIndexAtPerc];
      }

      series.appendRange(xValues, yValues);
    }
  }

  updateWaterfallChart(sciChart: SciChartSurface, wasmContext: TSciChart) {
    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    const xFactor =
      (this.tempData.CRE - this.tempData.CRB) / (endDataPoint - startDataPoint);

    //console.log('endRange: ' + xFactor);

    const heatMapData = zeroArray2D([
      this.temperatureDataRows.length,
      endDataPoint - startDataPoint,
    ]);

    for (let i = 0; i < this.temperatureDataRows.length; i++) {
      // for (let j = startRange; j < endRange; j++) {
      //   heatMapData[i][j - startRange] = this.temperatureDataRows[i].TR[j];
      // }
      for (let j = startDataPoint; j < endDataPoint; j++) {
        heatMapData[i][j - startDataPoint] = this.temperatureDataRows[i].TR[j];
      }
    }

    this.heatMaps = heatMapData;

    this.heatmapDataSeries1 = new UniformHeatmapDataSeries(wasmContext, {
      xStart: 0,
      xStep: xFactor,
      //yStart: this.temperatureDataRows[0].UT,
      yStart: 0,
      yStep: this.measurementInterval,
      zValues: this.heatMaps,
    });

    this.zoneDataLoaded = true;

    if (this.temperatureDataRows.length > 0) {
      this.temp_startUnixTime = this.temperatureDataRows[0].UT;
    }
  }

  formatDurationInSecToHHMMSS_Custom2(durationSec: number) {
    const hours = Math.floor(durationSec / 3600);

    var days = Math.floor(hours / 24);
    var rem_hours = hours % 24;

    const minutes = '0' + Math.floor((durationSec % 3600) / 60);
    const seconds = '0' + Math.floor((durationSec % 3600) % 60);

    if (isNaN(hours)) {
      return '';
    }

    var formattedTime =
      rem_hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);
    if (days > 0) {
      return days + '.' + formattedTime;
    } else {
      return formattedTime;
    }
  }

  getHours(unixTimestamp: number): number {
    var date;

    date = new Date(unixTimestamp);

    const hours = date.getUTCHours();
    if (isNaN(hours)) {
      return 0;
    }
    return hours;
  }

  getTimeDiffInSec(startUnix: number, endUnix: number): number {
    var date1, date2;

    date1 = new Date(startUnix);
    date2 = new Date(endUnix);

    var seconds = (date2.getTime() - date1.getTime()) / 1000;
    if (isNaN(seconds)) {
      return 0;
    }
    return seconds;
  }

  setupZoneDataSeries(
    sciChartSurface: SciChartSurface,
    wasmContext: TSciChart,
    heatMapDataSeries: UniformHeatmapDataSeries
  ) {
    const heatmapSeries = new UniformHeatmapRenderableSeries(wasmContext, {
      dataSeries: heatMapDataSeries,
      colorMap: new HeatmapColorMap({
        minimum: this.projectService.unit_Temp_Val(-10), // min value in the zValues (data) to map to offset 0 in the colormap. see GraphViewModel.cs ZoneGradient is define there and is hard coded.
        maximum: this.projectService.unit_Temp_Val(180), // max value in the zValues (data) to map to offset 1 in the colormap
        gradientStops: [
          // { offset: _offsets.coldOffset, color: "#0000FF" }, //blue ColdOffset
          // { offset: _offsets.heatingOffset, color: "#FFA500" }, //orange HeatingOffset
          // { offset: _offsets.curingOffset, color: "#32CD32" }, //green CuringOffset
          // { offset: _offsets.overheatOffset, color: "#FF0000" }, //red OverheatOffset
          { offset: 0, color: '#0000FF' }, //blue ColdOffset
          { offset: 0.194444444444445, color: '#FFA500' }, //orange HeatingOffset
          { offset: 0.388888888888889, color: '#32CD32' }, //green CuringOffset
          { offset: 1, color: '#FF0000' }, //red OverheatOffset
        ],
      }),
    });

    const customTemplate = (heatmapSeries3d: any, tooltipTitle: any) => {
      const valuesWithLabels = [];
      if (tooltipTitle) {
        valuesWithLabels.push(tooltipTitle);
      }
      const heatmapSeriesInfo = heatmapSeries3d;
      valuesWithLabels.push(
        `${this.formatDurationInSecToHHMMSS_Custom2(
          heatmapSeriesInfo.formattedYValue
        )}`
      );
      valuesWithLabels.push(
        `${heatmapSeriesInfo.formattedZValue}` +
          (this.projectService.isMetricUnit
            ? String.fromCharCode(176).concat('C')
            : String.fromCharCode(176).concat('F'))
      );
      return valuesWithLabels;
    };
    this.rolloverModifier = new RolloverModifier({
      tooltipDataTemplate: customTemplate,
    });
    sciChartSurface.chartModifiers.add(this.rolloverModifier);
    heatmapSeries.rolloverModifierProps.tooltipTextColor = 'Black';

    // Create new modifier instances to avoid "already attached" errors
    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    sciChartSurface.renderableSeries.add(heatmapSeries);

    this.right_onRangeSliderChange = (top, bot) => {
      //console.log('Series legend right:' + top + '-' + bot);
      const tempMax = this.projectService.unit_Temp_Val(180);
      //const tempMin = this.projectService.unit_Temp_Val(170);
      const tempMin = this.projectService.unit_Temp_Val(-10);

      //console.log('Temp Min-Max:' + tempMin + ' to ' + tempMax);
      const visibleMax =
        tempMax - Math.round((top / 100) * (tempMax - tempMin));
      const visibleMin =
        tempMin + Math.round(((100 - bot) / 100) * (tempMax - tempMin));
      //console.log('Series Min-Max:' + visibleMin + ' to ' + visibleMax);

      console;
      heatmapSeries.colorMap.maximum = visibleMax;
      heatmapSeries.colorMap.minimum = visibleMin;
    };
  }

  async createTempLegend() {
    var options = {
      yAxisOptions: {
        axisBorder: {
          borderLeft: 1,
          color: '#FFFFFF77',
        },
        majorTickLineStyle: {
          color: 'White',
          tickSize: 6,
          strokeThickness: 1,
        },
        minorTickLineStyle: {
          color: 'White',
          tickSize: 3,
          strokeThickness: 1,
        },
        isVisible: false,
      },
      colorMap: {
        minimum: this.projectService.unit_Temp_Val(-10),
        maximum: this.projectService.unit_Temp_Val(180),
        gradientStops: [
          { offset: 0, color: '#0000FF' }, //blue ColdOffset
          { offset: 0.194444444444445, color: '#FFA500' }, //orange HeatingOffset
          { offset: 0.388888888888889, color: '#32CD32' }, //green CuringOffset
          { offset: 1, color: '#FF0000' }, //red OverheatOffset
        ],
      },
    };

    const { heatmapLegend, wasmContext } = await HeatmapLegend.create(
      'temp-legend',
      options
    );

    const yAxis = new NumericAxis(wasmContext, {
      autoRange: EAutoRange.Once,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 0,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
      flippedCoordinates: false,
    });

    const legendSciChartSurface =
      heatmapLegend.innerSciChartSurface.sciChartSurface;

    legendSciChartSurface.yAxes.add(yAxis);

    this.right2_onRangeSliderChange = (top, bot) => {
      const tempMax = this.projectService.unit_Temp_Val(180);
      const tempMin = this.projectService.unit_Temp_Val(170);
      const visibleMax = tempMax - Math.round((top / 100) * tempMax);
      const visibleMin = tempMin - Math.round((bot / 100) * tempMax);

      yAxis.visibleRange = new NumberRange(visibleMin, visibleMax);
    };
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  convertRgbStringToColor(rgbString: string): string {
    // Convert "r, g, b" string to hex format for SciChart
    if (!rgbString) {
      return '#7F30F3'; // Default color
    }
    
    // If it's already in hex format, return as is
    if (rgbString.startsWith('#')) {
      return rgbString;
    }
    
    // Parse "r, g, b" string and convert to hex
    const parts = rgbString.split(',').map(s => s.trim());
    if (parts.length !== 3) {
      return '#7F30F3'; // Default color if format is invalid
    }
    
    const r = parseInt(parts[0]);
    const g = parseInt(parts[1]);
    const b = parseInt(parts[2]);
    
    // Convert to hex format
    const toHex = (n: number) => {
      const hex = n.toString(16).padStart(2, '0');
      return hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  updateVerticalAnnotations(sciChart: SciChartSurface, markers: MarkerInfo[]) {
    console.log('GraphComponent > updateVerticalAnnotations called with markers:', markers);
    
    // Clear all marker annotations
    sciChart.annotations.clear();
    
    if (!markers || markers.length === 0) {
      console.log('GraphComponent > No markers to add');
      return;
    }
    
    markers.forEach((mar, index) => {
      // Convert RGB string "r, g, b" to format SciChart understands
      const colorString = this.convertRgbStringToColor(mar.C);
      
      const annt = new VerticalLineAnnotation({
        labelPlacement: ELabelPlacement.Axis,
        showLabel: true,
        stroke: colorString,
        strokeThickness: 3.1,
        x1: mar.P,
        axisLabelFill: colorString,
        axisFontSize: 14,
        isEditable: true,
        isSelected: false,
        onDragStarted: () => {
          // Increase thickness during drag for better visibility
          annt.strokeThickness = 6.5;
        },
        onDragEnded: () => {
          // Update the position in the internal array
          mar.P = +annt.x1.toFixed(2);
          // Persist to API
          this.projectService.updateVerticalAnnotationPosition(
            index,
            +annt.x1.toFixed(2)
          );
          // Reset to normal thickness after drag
          annt.strokeThickness = 3.1;
        },
      });

      sciChart.annotations.add(annt);
    });
  }

  updateChartVisibleRange(leftVal: string, rightVal: string) {
    if (!this.onRangeSliderChange) return;

    if (+leftVal <= +rightVal) {
      this.rangeSliderLeftVal = +leftVal;
      this.rangeSliderRightVal = +rightVal;
    } else {
      this.rangeSliderLeftVal = +rightVal;
      this.rangeSliderRightVal = +leftVal;
    }

    this.onRangeSliderChange(this.rangeSliderLeftVal, this.rangeSliderRightVal);
  }

  left_updateChartVisibleRange(
    botVal: string,
    topVal: string,
    isTopThumb: boolean
  ) {
    var iTopVal = +topVal;
    var iBotVal = +botVal;

    //console.log(iTopVal + '---' + iBotVal);

    if (isTopThumb) {
      //console.log('isTopThumb');
      iTopVal = 100 - iTopVal;
      iBotVal = 100 - +botVal;
    } else {
      iTopVal = 100 - +topVal;
      iBotVal = 100 - iBotVal;
    }

    //console.log(iTopVal + '===' + iBotVal);

    //console.log(iLeftVal + '---' + iRightVal);
    if (!this.left_onRangeSliderChange) return;
    if (+iTopVal <= +iBotVal) {
      this.left_rangeSliderTopVal = iTopVal;
      this.left_rangeSliderBotVal = iBotVal;
    } else {
      this.left_rangeSliderTopVal = iBotVal;
      this.left_rangeSliderBotVal = iTopVal;
    }
    this.left_onRangeSliderChange(
      this.left_rangeSliderTopVal,
      this.left_rangeSliderBotVal
    );
  }

  right_updateChartVisibleRange(
    botVal: string,
    topVal: string,
    isTopThumb: boolean
  ) {
    var iTopVal = +topVal;
    var iBotVal = +botVal;
    //console.log(iTopVal + '---' + iBotVal);

    if (isTopThumb) {
      console.log('isTopThumb');
      iTopVal = 100 - iTopVal;
      iBotVal = 100 - +botVal;
    } else {
      iTopVal = 100 - +topVal;
      iBotVal = 100 - iBotVal;
    }

    //console.log(iTopVal + '===' + iBotVal);

    //console.log(iLeftVal + '---' + iRightVal);
    if (!this.right_onRangeSliderChange) return;
    if (+iTopVal <= +iBotVal) {
      this.right_rangeSliderLeftVal = iTopVal;
      this.right_rangeSliderRightVal = iBotVal;
    } else {
      this.right_rangeSliderLeftVal = iBotVal;
      this.right_rangeSliderRightVal = iTopVal;
    }
    this.right_onRangeSliderChange(
      this.right_rangeSliderLeftVal,
      this.right_rangeSliderRightVal
    );

    if (!this.right2_onRangeSliderChange) return;
    this.right2_onRangeSliderChange(
      this.right_rangeSliderLeftVal,
      this.right_rangeSliderRightVal
    );
  }

  toggleRangeSlider() {
    this.showRangeSlider = !this.showRangeSlider;

    if (!this.onRangeSliderChange) return;
    this.onRangeSliderChange(0, 100);

    if (!this.left_onRangeSliderChange) return;
    this.left_onRangeSliderChange(0, 100);

    if (!this.right_onRangeSliderChange) return;
    this.right_onRangeSliderChange(0, 100);

    if (!this.right2_onRangeSliderChange) return;
    this.right2_onRangeSliderChange(0, 100);
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }
}
